/* Display-only cart offer rules. No requests or cart mutations occur here. */
(function (root, factory) {
  'use strict';
  var rules = factory();
  if (typeof module === 'object' && module.exports) module.exports = rules;
  if (root) root.LFCartOfferRules = rules;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function cents(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
  }

  function sku(value) {
    return String(value == null ? '' : value).trim().replace(/\.+$/, '').toUpperCase();
  }

  function variantId(item) {
    return String(item && (item.variant_id != null ? item.variant_id : item.id) || '');
  }

  function planId(item) {
    var allocation = item && item.selling_plan_allocation;
    var plan = allocation && allocation.selling_plan;
    var id = plan && typeof plan === 'object' ? plan.id : plan;
    if (id == null && allocation) id = allocation.selling_plan_id;
    if (id == null && item) id = item.selling_plan;
    if (id && typeof id === 'object') id = id.id;
    return id == null || id === '' ? null : String(id);
  }

  function sortedValue(value) {
    if (Array.isArray(value)) return value.map(sortedValue);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().map(function (key) {
        return [key, sortedValue(value[key])];
      });
    }
    return value;
  }

  function identity(item) {
    item = item || {};
    return JSON.stringify({
      id: variantId(item),
      quantity: item.quantity,
      planid: planId(item),
      properties: sortedValue(item.properties || {})
    });
  }

  function fingerprint(cart) {
    return JSON.stringify(((cart && cart.items) || []).map(identity).sort());
  }

  function sameVariant(item, product) {
    return Boolean(product && product.variantId != null && variantId(item) === String(product.variantId));
  }

  function inFamily(item, product) {
    var familySku = product && sku(product.sku);
    return Boolean(familySku && sku(item.sku) === familySku) || sameVariant(item, product);
  }

  function familyLines(items, product) {
    return items.filter(function (item) { return inFamily(item, product); });
  }

  function familyQuantity(items, product) {
    return familyLines(items, product).reduce(function (total, item) { return total + item.quantity; }, 0);
  }

  function available(product) {
    return Boolean(product && product.id != null && product.variantId != null &&
      product.available === true && cents(product.price));
  }

  function nonempty(value) {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  }

  function bundled(item) {
    return Boolean(item.has_components || nonempty(item.parent_relationship) ||
      nonempty(item.item_components) ||
      (item.properties && Object.prototype.hasOwnProperty.call(item.properties, '__moonbundle')));
  }

  function discounted(value) {
    return Number(value.total_discount) > 0 || nonempty(value.cart_level_discount_applications) ||
      nonempty(value.discount_applications) || nonempty(value.discounts) ||
      nonempty(value.line_level_discount_allocations);
  }

  function upgradeSource(items, product) {
    if (!product || !cents(product.price)) return null;
    var matches = familyLines(items, product);
    if (matches.length !== 1) return null;
    var item = matches[0];
    if (!sameVariant(item, product) || item.quantity !== 2 ||
      item.selling_plan_allocation || planId(item) !== null || nonempty(item.properties) ||
      item.final_price !== product.price) return null;
    if (item.final_line_price != null && item.final_line_price !== 2 * product.price) return null;
    return item;
  }

  function upgrade(id, target, source, price, properties) {
    return {
      id: id,
      kind: 'upgrade',
      target: target,
      source: source,
      price: price,
      increment: price - source.quantity * source.final_price,
      properties: properties || {}
    };
  }

  function add(id, target) {
    return {
      id: id,
      kind: 'add',
      target: target,
      source: null,
      price: target.price,
      increment: target.price,
      properties: {}
    };
  }

  function select(cart, config, moonOffer) {
    if (!cart || !config || config.eligible !== true || cart.currency !== 'USD' ||
      !Array.isArray(cart.items) || cart.items.length === 0) return null;
    var items = cart.items;
    if (items.some(function (item) {
      return !item || !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
        bundled(item);
    })) return null;
    // Discounts should not hide an optional add-on. Replacements still require
    // exact net-price verification; fall back to an add-on for discounted carts.
    var hasDiscount = discounted(cart) || items.some(discounted);
    var products = config.products || {};
    var source = !hasDiscount && upgradeSource(items, products.sg);
    var moonProperties = moonOffer && moonOffer.properties;
    if (source && available(products.sg3) && moonOffer && cents(moonOffer.price) &&
      moonOffer.price > 2 * products.sg.price && moonOffer.price < 3 * products.sg.price &&
      moonProperties && typeof moonProperties.__moonbundle === 'string' && moonProperties.__moonbundle.trim()) {
      return upgrade('sg-three', products.sg3, source, moonOffer.price, {
        __moonbundle: moonProperties.__moonbundle
      });
    }
    source = !hasDiscount && upgradeSource(items, products.omegas);
    if (source && available(products.omegas3) &&
      products.omegas3.price > 2 * products.omegas.price && products.omegas3.price < 3 * products.omegas.price) {
      return upgrade('omegas-three', products.omegas3, source, products.omegas3.price);
    }
    if (familyQuantity(items, products.sg) > 0 && familyQuantity(items, products.protein) === 0 &&
      available(products.protein)) return add('add-protein', products.protein);
    if (familyQuantity(items, products.protein) > 0 && familyQuantity(items, products.sg) === 0 &&
      available(products.sg)) return add('add-greens', products.sg);
    if (familyQuantity(items, products.sg) > 0 && familyQuantity(items, products.protein) > 0 &&
      familyQuantity(items, products.omegas) === 0 && available(products.omegas)) {
      return add('add-omegas', products.omegas);
    }
    return null;
  }

  return { select: select, identity: identity, fingerprint: fingerprint };
});
